import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { api, getPropertyDocumentDownloadUrl } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  return parseFloat(String(v)) || 0;
}

export const InvestorPropertyAccountStatements: React.FC = () => {
  const { id: investorId, propertyId } = useParams<{ id: string; propertyId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const { data, isLoading, error } = useQuery({
    queryKey: ['investor-statements', investorId],
    queryFn: () => api.getInvestorStatements(investorId!),
    enabled: !!investorId,
    staleTime: 30_000,
  });

  const statements = useMemo(() => {
    const rows = Array.isArray(data) ? (data as any[]) : [];
    return rows
      .filter((r) => String((r as any).propertyId) === String(propertyId))
      .sort((a, b) => {
        const ay = Number((a as any).year);
        const by = Number((b as any).year);
        if (ay !== by) return by - ay;
        return Number((b as any).month) - Number((a as any).month);
      });
  }, [data, propertyId]);

  const propertyName = useMemo(() => {
    const first = statements[0] as any;
    return first?.property?.name ?? first?.propertyName ?? 'Propiedad';
  }, [statements]);

  const formatCurrency = (value: number) =>
    canViewPrices ? `$${Number(value).toLocaleString('es-MX')}` : '—';

  const formatMonthYear = (month: number, year: number) => {
    const d = new Date(year, month - 1, 1);
    return d
      .toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
        {error instanceof Error ? error.message : 'Error al cargar estados de cuenta'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate(investorId ? `/inversionistas/${investorId}` : '/inversionistas')}
          >
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{propertyName}</h1>
            <p className="text-sm text-gray-500">Estados de cuenta por mes</p>
          </div>
        </div>
      </div>

      {statements.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            No hay estados de cuenta registrados para esta propiedad.
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {statements.map((s: any) => (
            <Card key={s.id} title={formatMonthYear(Number(s.month), Number(s.year))}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Ingreso</p>
                  <p className="text-2xl font-semibold text-green-600">{formatCurrency(num(s.income))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Egreso</p>
                  <p className="text-2xl font-semibold text-red-500">{formatCurrency(num(s.expenses))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Ingreso neto</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatCurrency(num(s.netIncome))}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Utilidad</p>
                  <p className="text-2xl font-semibold text-primary">{formatCurrency(num(s.investorShare))}</p>
                </div>
              </div>

              {s.fileUrl && (
                <div className="pt-4 flex justify-end">
                  <a
                    href={getPropertyDocumentDownloadUrl(s.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                  >
                    <Download className="w-4 h-4" />
                    Descargar reporte
                  </a>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

